import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';
import { useGetCompany } from '@/services';
import { EntityGroupCard } from '@/pages/admin/shared/entity-management/EntityGroupCard';
import { EntityManagementSection } from '@/pages/admin/shared/entity-management/EntityManagementSection';
import {
	getEntityIconStyleForIndex,
	getEntityInitials,
} from '@/pages/admin/shared/entity-management/entityVisuals';
import { LinkOrgUnitModal } from './components/LinkOrgUnitModal';
import { CreateDepartmentModal } from './components/CreateDepartmentModal';
import { CreateSectorModal } from './components/CreateSectorModal';
import { EditDepartmentModal } from './components/EditDepartmentModal';
import { EditSectorModal } from './components/EditSectorModal';
import { DepartmentDetailsModal } from './components/DepartmentDetailsModal';
import { DepartmentMembersModal } from './components/DepartmentMembersModal';
import { LinkCollaboratorOrgModal } from './components/LinkCollaboratorOrgModal';
import { ConfirmDeleteModal } from './components/ConfirmDeleteModal';
import {
	useCreateCompanyDepartment,
	useCreateCompanySector,
	useLinkCompanyDepartment,
	usePatchCompanyDepartment,
	usePatchCompanySector,
	useRemoveCompanyDepartment,
	useRemoveCompanySector,
	useUnlinkCompanyDepartment,
} from '@/services/mutations/company-department';
import {
	useGetCompanyDepartment,
	useGetCompanyDepartments,
} from '@/services/queries/company-department';

type DepartmentView = {
	id: string;
	name: string;
	sectors: Array<{ id: string; name: string }>;
};

type DeleteTarget =
	| { type: 'department'; departmentId: string; departmentName: string }
	| {
			type: 'sector';
			departmentId: string;
			departmentName: string;
			sectorId: string;
			sectorName: string;
	  }
	| { type: 'unlink'; departmentId: string; departmentName: string };

type CompanyDepartmentsPageProps = {
	companyId?: string;
};

const toDepartmentView = (
	department: CompanyDepartments.CompanyDepartment,
): DepartmentView => ({
	id: department.id,
	name: department.name,
	sectors: (department.company_sector ?? []).map((sector) => ({
		id: sector.id,
		name: sector.name,
	})),
});

export const CompanyDepartmentsPage = ({
	companyId,
}: CompanyDepartmentsPageProps) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [isDeptModalOpen, setIsDeptModalOpen] = useState(false);
	const [isSectorModalOpen, setIsSectorModalOpen] = useState(false);
	const [isLinkDepartmentModalOpen, setIsLinkDepartmentModalOpen] =
		useState(false);
	const [sectorModalDefaultDepartmentId, setSectorModalDefaultDepartmentId] =
		useState<string | undefined>(undefined);
	const [editDepartmentId, setEditDepartmentId] = useState<string | null>(
		null,
	);
	const [editSector, setEditSector] = useState<{
		departmentId: string;
		sectorId: string;
		departmentName: string;
		sectorName: string;
	} | null>(null);
	const [detailsDepartmentId, setDetailsDepartmentId] = useState<
		string | null
	>(null);
	const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
	const [membersDepartment, setMembersDepartment] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [isLinkCollaboratorModalOpen, setIsLinkCollaboratorModalOpen] =
		useState(false);
	const [linkOrgPrefillEmployeeId, setLinkOrgPrefillEmployeeId] = useState<
		string | null
	>(null);

	const { data: company } = useGetCompany(companyId ?? '', {
		enabled: !!companyId,
	});

	const { data: allDepartmentsResponse, refetch: refetchAllDepartments } =
		useGetCompanyDepartments({
			page: 1,
			limit: 100,
		});
	const {
		data: linkedDepartmentsResponse,
		isFetching: isFetchingDepartments,
		refetch: refetchLinkedDepartments,
	} = useGetCompanyDepartments(
		{
			page: 1,
			limit: 100,
			company_id: companyId,
		},
		{
			enabled: !!companyId,
		},
	);
	const { data: detailsDepartmentResponse } = useGetCompanyDepartment(
		detailsDepartmentId ?? '',
		{
			enabled: !!detailsDepartmentId,
			refetchOnWindowFocus: false,
		},
	);

	const createDepartmentMutation = useCreateCompanyDepartment();
	const patchDepartmentMutation = usePatchCompanyDepartment();
	const removeDepartmentMutation = useRemoveCompanyDepartment();
	const linkDepartmentMutation = useLinkCompanyDepartment();
	const unlinkDepartmentMutation = useUnlinkCompanyDepartment();
	const createSectorMutation = useCreateCompanySector();
	const patchSectorMutation = usePatchCompanySector();
	const removeSectorMutation = useRemoveCompanySector();

	const departments = useMemo(() => {
		const source =
			companyId && linkedDepartmentsResponse
				? linkedDepartmentsResponse.data
				: allDepartmentsResponse?.data ?? [];
		return source.map(toDepartmentView);
	}, [allDepartmentsResponse, companyId, linkedDepartmentsResponse]);

	const linkedDepartments = useMemo(() => {
		return (linkedDepartmentsResponse?.data ?? []).map(toDepartmentView);
	}, [linkedDepartmentsResponse]);

	const linkedDepartmentIds = useMemo(() => {
		return linkedDepartments.map((department) => department.id);
	}, [linkedDepartments]);

	const deleteModalMeta = useMemo(() => {
		if (!deleteTarget) return null;
		if (deleteTarget.type === 'department' && companyId) {
			return {
				title: 'Desvincular departamento',
				description: (
					<>
						Tem certeza que deseja desvincular o departamento{' '}
						<strong className="text-stone-800">
							{deleteTarget.departmentName}
						</strong>{' '}
						desta empresa?
					</>
				),
			};
		}
		if (deleteTarget.type === 'department') {
			return {
				title: 'Excluir departamento',
				description: (
					<>
						Tem certeza que deseja excluir o departamento{' '}
						<strong className="text-stone-800">
							{deleteTarget.departmentName}
						</strong>
						? Todos os setores deste departamento serão removidos.
					</>
				),
			};
		}
		if (deleteTarget.type === 'unlink') {
			return {
				title: 'Desvincular departamento',
				description: (
					<>
						Tem certeza que deseja desvincular o departamento{' '}
						<strong className="text-stone-800">
							{deleteTarget.departmentName}
						</strong>{' '}
						desta empresa?
					</>
				),
			};
		}
		return {
			title: 'Excluir setor',
			description: (
				<>
					Tem certeza que deseja excluir o setor{' '}
					<strong className="text-stone-800">
						{deleteTarget.sectorName}
					</strong>{' '}
					do departamento{' '}
					<strong className="text-stone-800">
						{deleteTarget.departmentName}
					</strong>
					?
				</>
			),
		};
	}, [companyId, deleteTarget]);

	const editingDepartment = useMemo(() => {
		if (!editDepartmentId) return null;
		return departments.find(
			(department) => department.id === editDepartmentId,
		);
	}, [departments, editDepartmentId]);

	const detailsDepartment = useMemo(() => {
		if (!detailsDepartmentId) return null;
		if (detailsDepartmentResponse) {
			return toDepartmentView(detailsDepartmentResponse);
		}
		return (
			departments.find(
				(department) => department.id === detailsDepartmentId,
			) ?? null
		);
	}, [departments, detailsDepartmentId, detailsDepartmentResponse]);

	const detailsDepartmentIndex = useMemo(() => {
		if (!detailsDepartment?.id) return 0;
		const index = departments.findIndex(
			(department) => department.id === detailsDepartment.id,
		);
		return index >= 0 ? index : 0;
	}, [departments, detailsDepartment]);

	const membersDepartmentIndex = useMemo(() => {
		if (!membersDepartment) return 0;
		const index = departments.findIndex(
			(department) => department.id === membersDepartment.id,
		);
		return index >= 0 ? index : 0;
	}, [departments, membersDepartment]);

	const departmentOptions = useMemo(
		() =>
			departments.map((department) => ({
				id: department.id,
				name: department.name,
			})),
		[departments],
	);

	const filteredDepartments = useMemo(() => {
		const query = searchTerm.toLowerCase().trim();
		if (!query) return departments;
		return departments.filter((department) => {
			if (department.name.toLowerCase().includes(query)) return true;
			return department.sectors.some((sector) =>
				sector.name.toLowerCase().includes(query),
			);
		});
	}, [departments, searchTerm]);

	const linkCatalog = useMemo(() => {
		return (allDepartmentsResponse?.data ?? []).map((department) => ({
			id: department.id,
			name: department.name,
			code: department.id.slice(0, 8).toUpperCase(),
			description:
				department.company_sector &&
				department.company_sector.length > 0
					? `${department.company_sector.length} setor(es) cadastrado(s)`
					: 'Sem setores cadastrados',
		}));
	}, [allDepartmentsResponse]);

	const openSectorModal = (departmentId?: string) => {
		if (departments.length === 0) {
			toast.info('Crie ou vincule um departamento primeiro.');
			return;
		}
		setSectorModalDefaultDepartmentId(departmentId);
		setIsSectorModalOpen(true);
	};

	const closeSectorModal = () => {
		setIsSectorModalOpen(false);
		setSectorModalDefaultDepartmentId(undefined);
	};

	const handleCreateDepartment = async (name: string) => {
		const createdDepartment = await createDepartmentMutation.mutateAsync({
			name,
		});
		if (companyId) {
			await linkDepartmentMutation.mutateAsync({
				departmentId: createdDepartment.id,
				body: { company_id: companyId },
			});
		}
		await refetchAllDepartments();
		if (companyId) {
			await refetchLinkedDepartments();
		}
		setIsDeptModalOpen(false);
	};

	const handleCreateSector = async (
		departmentId: string,
		sectorName: string,
	) => {
		await createSectorMutation.mutateAsync({
			departmentId,
			body: { name: sectorName },
		});
		await refetchAllDepartments();
		if (companyId) {
			await refetchLinkedDepartments();
		}
		closeSectorModal();
	};

	const handleUpdateDepartment = async (id: string, name: string) => {
		await patchDepartmentMutation.mutateAsync({
			id,
			body: { name },
		});
		await refetchAllDepartments();
		if (companyId) {
			await refetchLinkedDepartments();
		}
		setEditDepartmentId(null);
	};

	const handleUpdateSector = async (
		departmentId: string,
		sectorId: string,
		name: string,
	) => {
		await patchSectorMutation.mutateAsync({
			departmentId,
			sectorId,
			body: { name },
		});
		await refetchAllDepartments();
		if (companyId) {
			await refetchLinkedDepartments();
		}
		setEditSector(null);
	};

	const handleConfirmDelete = async () => {
		if (!deleteTarget) return;
		if (deleteTarget.type === 'department') {
			if (companyId) {
				await unlinkDepartmentMutation.mutateAsync({
					departmentId: deleteTarget.departmentId,
					companyId,
				});
			} else {
				await removeDepartmentMutation.mutateAsync(
					deleteTarget.departmentId,
				);
			}
		} else if (deleteTarget.type === 'unlink') {
			if (!companyId) return;
			await unlinkDepartmentMutation.mutateAsync({
				departmentId: deleteTarget.departmentId,
				companyId,
			});
		} else {
			await removeSectorMutation.mutateAsync({
				departmentId: deleteTarget.departmentId,
				sectorId: deleteTarget.sectorId,
			});
		}
		await refetchAllDepartments();
		if (companyId) {
			await refetchLinkedDepartments();
		}
		setDeleteTarget(null);
	};

	const handleLinkDepartment = async (departmentId: string) => {
		if (!companyId) return;
		await linkDepartmentMutation.mutateAsync({
			departmentId,
			body: { company_id: companyId },
		});
		await refetchAllDepartments();
		await refetchLinkedDepartments();
		setIsLinkDepartmentModalOpen(false);
	};

	return (
		<>
			{companyId && (
				<div className="mb-6 rounded-2xl border border-stone-100 bg-white p-4 shadow-sm">
					<div className="flex items-center justify-between gap-3">
						<div>
							<h3 className="text-sm font-black uppercase tracking-widest text-stone-500">
								Departamentos vinculados
							</h3>
							<p className="mt-1 text-sm text-stone-500">
								Desvincule departamentos desta empresa sem
								apagar o cadastro global.
							</p>
						</div>
					</div>
					<div className="mt-4 flex flex-wrap gap-2">
						{linkedDepartments.length > 0 ? (
							linkedDepartments.map((department) => (
								<span
									key={`linked-${department.id}`}
									className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-semibold text-stone-700"
								>
									{department.name}
									<button
										type="button"
										className="rounded-full p-0.5 text-stone-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
										onClick={() =>
											setDeleteTarget({
												type: 'unlink',
												departmentId: department.id,
												departmentName: department.name,
											})
										}
										aria-label={`Desvincular ${department.name}`}
									>
										<X className="size-3.5" />
									</button>
								</span>
							))
						) : (
							<p className="text-sm text-stone-400">
								Nenhum departamento vinculado a esta empresa.
							</p>
						)}
					</div>
				</div>
			)}

			<EntityManagementSection
				className="space-y-6 pt-4"
				title="Departamentos"
				description="Organize sua empresa por departamentos e setores."
				searchPlaceholder="Buscar departamento ou setor..."
				searchValue={searchTerm}
				onSearchChange={setSearchTerm}
				reverseActionButtonsOrder
				primaryActionText="Novo departamento"
				onPrimaryAction={() => setIsDeptModalOpen(true)}
				secondaryActionText={
					companyId ? 'Vincular departamento' : 'Novo setor'
				}
				onSecondaryAction={
					companyId
						? () => setIsLinkDepartmentModalOpen(true)
						: () => openSectorModal(undefined)
				}
				hasAnyEntities={departments.length > 0}
				hasVisibleEntities={filteredDepartments.length > 0}
				emptyInitialTitle="Nenhum departamento ainda"
				emptyInitialDescription="Use “Novo departamento” para começar e depois adicione setores."
				emptyActionText="Novo departamento"
				onEmptyActionClick={() => setIsDeptModalOpen(true)}
			>
				{isFetchingDepartments && companyId ? (
					<p className="px-2 text-sm font-semibold text-stone-500">
						Carregando departamentos...
					</p>
				) : null}
				{filteredDepartments.map((department, index) => {
					const originalIndex = departments.findIndex(
						(d) => d.id === department.id,
					);
					const cardIndex =
						originalIndex >= 0 ? originalIndex : index;
					return (
						<EntityGroupCard
							key={department.id}
							entityId={department.id}
							entityName={department.name}
							index={cardIndex}
							initials={getEntityInitials(department.name)}
							iconClassName={getEntityIconStyleForIndex(
								cardIndex,
							)}
							childItems={department.sectors.map(
								(sector) => sector.name,
							)}
							childLabelSingular="SETOR"
							childLabelPlural="SETORES"
							emptyChildrenTitle="Nenhum setor ainda"
							emptyChildrenDescription="Adicione um setor a este departamento."
							createChildText="Novo setor"
							onEditEntity={() =>
								setEditDepartmentId(department.id)
							}
							onDeleteEntity={() =>
								setDeleteTarget({
									type: 'department',
									departmentId: department.id,
									departmentName: department.name,
								})
							}
							onEditChild={(sectorIndex) => {
								const sector = department.sectors[sectorIndex];
								if (!sector) return;
								setEditSector({
									departmentId: department.id,
									sectorId: sector.id,
									departmentName: department.name,
									sectorName: sector.name,
								});
							}}
							onRequestDeleteChild={(sectorIndex) => {
								const sector = department.sectors[sectorIndex];
								if (!sector) return;
								setDeleteTarget({
									type: 'sector',
									departmentId: department.id,
									departmentName: department.name,
									sectorId: sector.id,
									sectorName: sector.name,
								});
							}}
							onViewDetails={() =>
								setDetailsDepartmentId(department.id)
							}
							onCreateChild={() => openSectorModal(department.id)}
							alwaysShowDetailsButton
							onViewMembers={
								companyId
									? () =>
											setMembersDepartment({
												id: department.id,
												name: department.name,
											})
									: undefined
							}
						/>
					);
				})}
			</EntityManagementSection>

			<CreateDepartmentModal
				isOpen={isDeptModalOpen}
				onClose={() => setIsDeptModalOpen(false)}
				onCreate={(name) => void handleCreateDepartment(name)}
			/>

			<CreateSectorModal
				isOpen={isSectorModalOpen}
				onClose={closeSectorModal}
				departments={departmentOptions}
				defaultDepartmentId={sectorModalDefaultDepartmentId}
				onCreate={(departmentId, sectorName) =>
					void handleCreateSector(departmentId, sectorName)
				}
			/>

			<EditDepartmentModal
				isOpen={!!editingDepartment}
				onClose={() => setEditDepartmentId(null)}
				initialName={editingDepartment?.name ?? ''}
				onSave={(name) => {
					if (editDepartmentId) {
						void handleUpdateDepartment(editDepartmentId, name);
					}
				}}
			/>

			<EditSectorModal
				isOpen={!!editSector}
				onClose={() => setEditSector(null)}
				departmentName={editSector?.departmentName ?? ''}
				initialSectorName={editSector?.sectorName ?? ''}
				onSave={(name) => {
					if (editSector) {
						void handleUpdateSector(
							editSector.departmentId,
							editSector.sectorId,
							name,
						);
					}
				}}
			/>

			{membersDepartment && companyId ? (
				<DepartmentMembersModal
					isOpen
					onClose={() => setMembersDepartment(null)}
					departmentName={membersDepartment.name}
					initials={getEntityInitials(membersDepartment.name)}
					iconClassName={getEntityIconStyleForIndex(
						membersDepartmentIndex,
					)}
					members={(company?.employees ?? []).filter(
						(e) => e.department_id === membersDepartment.id,
					)}
					departmentLinks={company?.company_department_link}
					onRequestLinkOrg={(emp) => {
						setLinkOrgPrefillEmployeeId(emp.id);
						setIsLinkCollaboratorModalOpen(true);
					}}
					onRequestLinkCollaborator={() => {
						setLinkOrgPrefillEmployeeId(null);
						setIsLinkCollaboratorModalOpen(true);
					}}
				/>
			) : null}

			{companyId ? (
				<LinkCollaboratorOrgModal
					isOpen={isLinkCollaboratorModalOpen}
					onClose={() => {
						setIsLinkCollaboratorModalOpen(false);
						setLinkOrgPrefillEmployeeId(null);
					}}
					companyId={companyId}
					employees={company?.employees ?? []}
					prefilledEmployeeId={linkOrgPrefillEmployeeId}
				/>
			) : null}

			{detailsDepartment && (
				<DepartmentDetailsModal
					isOpen
					onClose={() => setDetailsDepartmentId(null)}
					departmentName={detailsDepartment.name}
					initials={getEntityInitials(detailsDepartment.name)}
					iconClassName={getEntityIconStyleForIndex(
						detailsDepartmentIndex,
					)}
					sectors={detailsDepartment.sectors.map(
						(sector) => sector.name,
					)}
					onEditSector={(sectorIndex) => {
						const sector = detailsDepartment.sectors[sectorIndex];
						if (!sector) return;
						setEditSector({
							departmentId: detailsDepartment.id,
							sectorId: sector.id,
							departmentName: detailsDepartment.name,
							sectorName: sector.name,
						});
					}}
					onDeleteSector={(sectorIndex) => {
						const sector = detailsDepartment.sectors[sectorIndex];
						if (!sector) return;
						setDeleteTarget({
							type: 'sector',
							departmentId: detailsDepartment.id,
							departmentName: detailsDepartment.name,
							sectorId: sector.id,
							sectorName: sector.name,
						});
					}}
					onCreateSector={() => openSectorModal(detailsDepartment.id)}
				/>
			)}

			<LinkOrgUnitModal
				isOpen={isLinkDepartmentModalOpen}
				onClose={() => setIsLinkDepartmentModalOpen(false)}
				kind="department"
				catalog={linkCatalog}
				linkedIds={linkedDepartmentIds}
				onLink={(departmentId) =>
					void handleLinkDepartment(departmentId)
				}
				isLinking={linkDepartmentMutation.isPending}
			/>

			<ConfirmDeleteModal
				isOpen={!!deleteModalMeta}
				onClose={() => setDeleteTarget(null)}
				onConfirm={() => void handleConfirmDelete()}
				title={deleteModalMeta?.title ?? ''}
				description={deleteModalMeta?.description ?? null}
			/>
		</>
	);
};
